import React from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { showBasicAlert } from "../../../../core/common/useAlert";
import { Avatar, Badge, Tag } from "antd";
import Datatable from "../../../../core/common/dataTable";
import {
  UserOutlined,
  CalendarOutlined,
  FileOutlined,
  EditOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

const ShareDetailsModal = ({ show, onClose, file, shareInfo }) => {
  console.log("🚀 ~ ShareDetailsModal ~ shareInfo:", shareInfo);
  if (!show || !file || !shareInfo) return null;

  const sharedUsersData = shareInfo.shared_with.map((share, index) => ({
    key: index,
    ...share,
  }));

  const columns = [
    {
      title: "User",
      dataIndex: "shared_user_info",
      key: "user",
      width: "25%",
      render: (userInfo, record) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-xs me-2">
            <Avatar
              size={32}
              src={userInfo?.profile_pic || "assets/img/placeholders/user.png"}
              alt={userInfo?.name}
              className="rounded-circle"
            >
              {userInfo?.name?.charAt(0) || "U"}
            </Avatar>
          </div>
          <div>
            <div className="fw-medium">
              {userInfo?.name || `User ${record.user_id}`}
            </div>
            <small className="text-muted">
              {userInfo?.email || "No email"}
            </small>
          </div>
        </div>
      ),
      sorter: (a, b) => {
        const nameA = a.shared_user_info?.name || "";
        const nameB = b.shared_user_info?.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Access Level",
      dataIndex: "access_type",
      key: "access_type",
      width: "15%",
      render: (accessType) => (
        <Tag
          color={accessType === "edit" ? "warning" : "processing"}
          icon={accessType === "edit" ? <EditOutlined /> : <EyeOutlined />}
          style={{
            borderRadius: "12px",
            padding: "2px 8px",
            fontSize: "12px",
          }}
        >
          {accessType === "edit" ? "Can Edit" : "Can View"}
        </Tag>
      ),
      filters: [
        { text: "Can Edit", value: "edit" },
        { text: "Can View", value: "view" },
      ],
      onFilter: (value, record) => record.access_type === value,
    },
    {
      title: "Shared On",
      dataIndex: "shared_at",
      key: "shared_at",
      width: "20%",
      render: (sharedAt) => (
        <div>
          <div className="fw-medium">
            {new Date(sharedAt).toLocaleDateString()}
          </div>
          <small className="text-muted">
            {new Date(sharedAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </small>
        </div>
      ),
      sorter: (a, b) =>
        new Date(a.shared_at).getTime() - new Date(b.shared_at).getTime(),
    },
    // {
    //   title: "Status",
    //   dataIndex: "share_status",
    //   key: "share_status",
    //   width: "15%",
    //   render: (status) => (
    //     <Tag
    //       color={status === "active" ? "success" : "default"}
    //       icon={
    //         status === "active" ? (
    //           <CheckCircleOutlined />
    //         ) : (
    //           <CloseCircleOutlined />
    //         )
    //       }
    //       style={{
    //         borderRadius: "12px",
    //         padding: "2px 8px",
    //         fontSize: "12px",
    //       }}
    //     >
    //       {status === "active" ? "Active" : "Inactive"}
    //     </Tag>
    //   ),
    //   filters: [
    //     { text: "Active", value: "active" },
    //     { text: "Inactive", value: "inactive" },
    //   ],
    //   onFilter: (value, record) => record.share_status === value,
    // },
  ];

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
      }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{
            borderRadius: "8px",
            border: "none",
          }}
        >
          {/* Header */}
          <div className="modal-header">
            <h4 className="modal-title">
              <i className="ti ti-share me-2"></i>
              Share Details: {file.name}
            </h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="modal-body">
            {/* File Info */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="file-icon text-center">
                  <ImageWithBasePath
                    src={`assets/img/icons/${getIconByMime(file)}`}
                    alt={file.name}
                    width={64}
                    height={64}
                  />
                </div>
              </div>
              <div className="col-md-9">
                <h5>{file.name}</h5>
                {/* <p className="text-muted mb-1">
                  <UserOutlined className="me-1" />
                  Owner:{" "}
                  {shareInfo?.shared_with[0]?.shared_by?.name ||
                    `User ${file.owner_id}`}
                </p> */}
                <p className="text-muted mb-1">
                  <CalendarOutlined className="me-1" />
                  Created: {formatDate(file.createdAt)}
                </p>
                <p className="text-muted mb-0">
                  <FileOutlined className="me-1" />
                  Size: {formatSize(Number(file.size))}
                </p>
              </div>
            </div>

            {/* Shared With Users List using Datatable */}
            <div className="card">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="ti ti-users me-2"></i>
                  Shared With
                </h6>
                <span className="badge bg-primary" style={{ fontSize: "12px" }}>
                  {shareInfo.shared_count} people
                </span>
              </div>
              <div className="card-body p-0">
                <Datatable
                  columns={columns}
                  dataSource={sharedUsersData}
                  pagination={{
                    pageSize: 5,
                    showSizeChanger: true,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} users`,
                  }}
                  scroll={{ x: "max-content" }}
                  size="small"
                  bordered
                />
              </div>
            </div>

            {/* Share Link Section */}
            {shareInfo.share_link && (
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <i className="ti ti-link me-2"></i>
                    Share Link
                  </h6>
                </div>
                <div className="card-body">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={shareInfo.share_link}
                      readOnly
                    />
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(shareInfo.share_link);
                        showBasicAlert("Link copied to clipboard!");
                      }}
                    >
                      <i className="ti ti-copy me-1"></i> Copy
                    </button>
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Anyone with this link can access the file based on their
                    permissions.
                  </small>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getIconByMime = (item) => {
  if (item.type === 1) return "folder-icon.svg";
  const extension = item.name?.split(".").pop()?.toLowerCase() || "";
  switch (extension) {
    case "pdf":
      return "pdf-icon.svg";
    case "doc":
    case "docx":
      return "file.svg";
    case "xls":
    case "xlsx":
    case "csv":
      return "xls-icon.svg";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
      return "image.svg";
    default:
      return "file.svg";
  }
};

const formatSize = (bytes) => {
  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024;
    i++;
  }
  return `${bytes.toFixed(2)} ${units[i]}`;
};

export default ShareDetailsModal;
